import { Row, Col, Typography, Card, Divider, Flex } from "antd";

const { Text } = Typography;

const yn = (v) => (v === "1" ? "Yes" : v === "0" ? "No" : "—");
const tx = (v) => (v && v.trim() !== "" ? v : "—");

const FieldRow = ({ label, value, type = "text" }) => {
  const display = type === "yesno" ? yn(value) : tx(value);
  return (
    <Row justify="space-between" style={{ marginBottom: 1 }} gap={8}>
      <Text style={{ fontSize: 10 }}>{label}</Text>
      <Text style={{ fontSize: 10 }}>{display}</Text>
    </Row>
  );
};

const Section = ({ n, title, children }) => (
  <Card
    size="small"
    style={{ border: "1px solid #ccc", height: "100%", borderRadius: 4 }}
    bodyStyle={{ padding: "4px 6px" }}
  >
    <Flex gap={4} align="center">
      <div
        style={{
          background: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          padding: "0 5px",
          fontSize: 9,
          fontWeight: 700,
          minWidth: 18,
          textAlign: "center",
          lineHeight: "16px",
        }}
      >
        0{n}
      </div>
      <Text
        strong
        style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.3 }}
      >
        {title}
      </Text>
    </Flex>
    <Divider style={{ margin: "3px 0", background: "#ccc" }} />
    {children}
  </Card>
);

const PrintMedicalHistory = ({
  state = {},
  clientinfo,
  clinicSubtitle = "Medical History & Pre-Operative Assessment",
}) => {
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      {/* Print-specific CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 8mm 8mm 8mm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important; }
          .print-root {
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>

      <div
        className="print-root"
        style={{
          fontFamily: "'Georgia', serif",
          padding: "6px 10px",
          maxWidth: 900,
          margin: "0 auto",
          background: "#fff",
        }}
      >
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
          <Col>
            <img src="/assets/images/logo.webp" alt="logo" width={140} />
          </Col>
          <Col style={{ textAlign: "right" }}>
            <Text style={{ fontSize: 9, color: "#555" }}>Date: </Text>
            <Text style={{ fontSize: 9 }}>{today}</Text>
          </Col>
        </Row>

        {/* Subtitle */}
        <div
          style={{
            textAlign: "center",
            background: "#1a1a2e",
            color: "#fff",
            padding: "2px 0",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 1,
            borderRadius: 3,
            marginBottom: 5,
          }}
        >
          {clinicSubtitle}
        </div>

        {/* Patient Info */}
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: 4,
            padding: "3px 8px",
            marginBottom: 5,
            background: "#f9f9f9",
          }}
        >
          <Row gutter={[8, 1]}>
            <Col span={24}>
              <Text style={{ fontSize: 10 }} strong>
                MRN:{" "}
              </Text>
              <Text style={{ fontSize: 10 }}>{clientinfo?.mrn ?? "N/A"}</Text>
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: 10 }} strong>
                Name:{" "}
              </Text>
              <Text style={{ fontSize: 10 }}>{clientinfo?.name ?? "N/A"}</Text>
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: 10 }} strong>
                Father's Name:{" "}
              </Text>
              <Text style={{ fontSize: 10 }}>
                {clientinfo?.fatherName ?? "N/A"}
              </Text>
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: 10 }} strong>
                Age:{" "}
              </Text>
              <Text style={{ fontSize: 10 }}>{clientinfo?.age ?? "N/A"}</Text>
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: 10 }} strong>
                Address:{" "}
              </Text>
              <Text style={{ fontSize: 10 }}>
                {clientinfo?.address ?? "N/A"}
              </Text>
            </Col>
          </Row>
        </div>

        {/* All Sections in tight grid */}
        <Row gutter={[5, 5]}>
          {/* Row 1 */}
          <Col span={12}>
            <Section n={1} title="Hair Fall History">
              <FieldRow
                label="Age when hair fall started"
                value={state.hairFallStartingAge}
              />
              <FieldRow
                label="Family history of hair fall"
                value={state.isFamilyDisease}
                type="yesno"
              />
              <FieldRow
                label="Previous treatments"
                value={state.anyPreviousTreatments}
              />
            </Section>
          </Col>
          <Col span={12}>
            <Section n={2} title="Known Health Conditions">
              <FieldRow label="General health" value={state.generalHealth} />
              <FieldRow
                label="Diabetes"
                value={state.isDiabetes}
                type="yesno"
              />
              <FieldRow label="Asthma" value={state.isAsthma} type="yesno" />
              <FieldRow label="IHD" value={state.isIHD} type="yesno" />
              <FieldRow
                label="Hypertension"
                value={state.isHypertension}
                type="yesno"
              />
              <FieldRow
                label="Hepatitis"
                value={state.isHepatitis}
                type="yesno"
              />
              <FieldRow
                label="Jaundice"
                value={state.isJaundice}
                type="yesno"
              />
              <FieldRow
                label="Epilepsy"
                value={state.isEpilepsy}
                type="yesno"
              />
              <FieldRow
                label="Skin / Scalp conditions"
                value={state.isSkinConditions}
              />
            </Section>
          </Col>

          {/* Row 2 */}
          <Col span={12}>
            <Section n={3} title="Surgical & Procedural History">
              <FieldRow
                label="Local anesthesia history"
                value={state.localAnesthesiaHistory}
              />
              <FieldRow
                label="Current infection"
                value={state.anyCurrentInfection}
                type="yesno"
              />
              <FieldRow
                label="Hospital admissions"
                value={state.hospitalAdmissions}
              />
              <FieldRow
                label="Previous surgeries"
                value={state.previousSurgeries}
              />
              <FieldRow label="Trauma" value={state.trauma} />

              <FieldRow label="Others" value={state.other} />
            </Section>
          </Col>
          <Col span={12}>
            <Row gutter={[5, 5]} style={{ height: "100%" }}>
              <Col span={24}>
                <Section n={4} title="Body Metrics">
                  <FieldRow label="Height (cm)" value={state.height} />
                  <FieldRow label="Weight (kg)" value={state.weight} />
                  <FieldRow label="BMI (kg/m²)" value={state.bmi} />
                </Section>
              </Col>
              <Col span={24}>
                <Section n={5} title="Examination">
                  <FieldRow label="Pulse (bpm)" value={state.pulse} />
                  <FieldRow label="Blood pressure" value={state.BP} />
                  <FieldRow label="BSR (mg/dL)" value={state.BSR} />
                  <FieldRow
                    label="Oxygen Levels (SpO2)"
                    value={state.oxygen_Level}
                  />
                </Section>
              </Col>
              <Col span={24}>
                <Section n={6} title="Labs">
                  <FieldRow label="Pre-Ops" value={state.anyPreOps} />
                  <FieldRow label="HIV" value={state.HIV} />
                  <FieldRow label="Hepatitis B" value={state.hepatitisB} />
                  <FieldRow label="Hepatitis C" value={state.hepatitisC} />
                </Section>
              </Col>
            </Row>
          </Col>

          {/* Row 3 */}
          <Col span={12}>
            <Section n={7} title="Medications & Lifestyle">
              <FieldRow
                label="Aspirin / NSAIDs"
                value={state.anyAspirin}
                type="yesno"
              />
              <FieldRow
                label="Steroids"
                value={state.anySteroids}
                type="yesno"
              />
              <FieldRow
                label="Anti-Coagulants"
                value={state.anyAntiCoagulants}
                type="yesno"
              />
              <FieldRow
                label="Cigarette smoking"
                value={state.smoking}
                type="yesno"
              />
              <FieldRow
                label="Antibiotic / TB / anti-viral course"
                value={state.antibioticCourse}
              />
              <FieldRow label="Drug Allergies" value={state.drug_allergies} />
              <FieldRow label="Food Allergies" value={state.food_allergies} />
              <FieldRow
                label="Bleeding Tendencies"
                value={state.bleedingTendencies}
              />
              <FieldRow
                label="Other substance use"
                value={state.otherSubstanceUses}
              />
              <FieldRow
                label="Other Medications"
                value={state.anyOtherMedications}
              />
            </Section>
          </Col>
          <Col span={12}>
            <Section n={8} title="Travel & Clinician Notes">
              <FieldRow
                label="Recent Travel History"
                value={state.recentTravelHistory}
              />
              <FieldRow
                label="Flight Date (upcoming travel)"
                value={state.upComingTravel}
              />
              <FieldRow label="Any Note" value={state.note} />
            </Section>
          </Col>
        </Row>
      </div>
    </>
  );
};

export { PrintMedicalHistory };
